import { CarListing } from '../../types/dealership';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Car } from 'lucide-react';

interface CarListingCardProps {
  listing: CarListing;
  onEditListing: (listing: CarListing) => void;
  onViewDetails: (listing: CarListing) => void;
}

export function CarListingCard({ listing, onEditListing, onViewDetails }: CarListingCardProps) {
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('CarListingCard: Edit button clicked for listing:', listing);
    console.log('CarListingCard: Calling onEditListing with listing ID:', listing.id);
    onEditListing(listing);
  };

  const handleViewDetails = () => {
    onViewDetails(listing);
  };

  return (
    <Card 
      className="h-full border-2 border-gray-200 hover:border-[#f91bdc] transition-all duration-200 cursor-pointer hover:shadow-lg"
      onClick={handleViewDetails}
    >
      <CardContent className="p-0">
        {/* Image */}
        <div className="relative h-48 sm:h-52 md:h-56 rounded-t-lg overflow-hidden bg-gray-100">
          {listing.uploadedImageUrls && listing.uploadedImageUrls.length > 0 ? (
            <>
              <img
                src={listing.uploadedImageUrls[0]}
                alt={`${listing.year || ''} ${listing.make || ''} ${listing.model || ''}`}
                className="w-full h-full object-cover object-center transition-transform duration-300 hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  // Fallback to placeholder on error
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const placeholder = target.nextElementSibling as HTMLDivElement;
                  if (placeholder) placeholder.style.display = 'flex';
                }}
              />
              {/* Error fallback placeholder (hidden by default) */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center" style={{ display: 'none' }}>
                <Car size={64} className="text-[#f91bdc]/60" />
              </div>
              {/* Image count badge */}
              {listing.uploadedImageUrls.length > 1 && (
                <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
                  {listing.uploadedImageUrls.length} photos
                </div>
              )}
            </>
          ) : (
            <div className="h-full bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center">
              <div className="text-[#f91bdc]/60 text-center">
                <Car size={64} className="mx-auto mb-2" />
                <p className="text-sm">No Image</p>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-1">{listing.model}</h3>
            <p className="text-sm text-gray-600">{listing.type}</p>
          </div>

          <div className="flex justify-between items-center">
            <div>
              <p className="font-bold text-xl text-[#f91bdc]">{listing.price}</p>
              <p className="text-xs text-gray-500">{listing.financingTerm}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Min Credit Score</p>
              <p className="font-semibold text-gray-800">{listing.creditScore}</p>
            </div>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">{listing.sellerInfo}</p>
            
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDetails();
                }}
                className="text-[#f91bdc] hover:underline text-sm font-medium"
              >
                See Details
              </button>
              <Button
                onClick={handleEditClick}
                className="ml-auto bg-[#f91bdc] hover:bg-[#e016c7] text-white px-4 py-1 rounded-lg text-sm"
              >
                Edit Listing
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}